import { useEffect, useState } from 'react';
import {
  COOKIE_CONSENT_EVENT,
  isConsentReady,
  readCookieCategories,
} from './categories';

type ConsentStatus = 'loading' | 'ready';

type UseCookieConsentResult = ReturnType<typeof readCookieCategories> & {
  // `'loading'` until consent resolves, then `'ready'`. Without this
  // signal, "consent script still loading" and "user denied this
  // category" both render as denial. That collapsing is the React-side
  // root cause of INC-956.
  status: ConsentStatus;
};

const buildResult = (): UseCookieConsentResult => ({
  ...readCookieCategories(),
  status: isConsentReady() ? 'ready' : 'loading',
});

export const useCookieConsent = (): UseCookieConsentResult => {
  const [result, setResult] = useState<UseCookieConsentResult>(() =>
    buildResult()
  );
  useEffect(() => {
    // Re-read on attach to close the gap between `useState`'s initial
    // snapshot (taken during render) and the listener being attached.
    // Without this, a consent event that fires between render and the
    // post-commit effect is missed, and `status` stays `'loading'`.
    setResult(buildResult());
    const onConsentChanged = () => {
      setResult(buildResult());
    };
    window.addEventListener(COOKIE_CONSENT_EVENT, onConsentChanged);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, onConsentChanged);
    };
  }, []);
  return result;
};
